import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Notifications.css';

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'info';
  title: string;
  description: string;
  timestamp: string;
  isUnread: boolean;
  iconType: 'swap' | 'alert' | 'reward' | 'security' | 'withdrawal';
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'success',
    title: 'Swap Completed',
    description: 'Your swap from 0.5 BTC to 10 ETH was successful',
    timestamp: '2 min ago',
    isUnread: true,
    iconType: 'swap'
  },
  {
    id: '2',
    type: 'warning',
    title: 'Price Alert',
    description: 'BTC reached your target price of $100,000',
    timestamp: '1 hour ago',
    isUnread: true,
    iconType: 'alert'
  },
  {
    id: '3',
    type: 'info',
    title: 'Reward Received',
    description: 'You earned 10 USDT in trading rewards',
    timestamp: '3 hours ago',
    isUnread: false,
    iconType: 'reward'
  },
  {
    id: '4',
    type: 'warning',
    title: 'Security Alert',
    description: 'New login detected from Chrome on Windows',
    timestamp: '1 day ago',
    isUnread: false,
    iconType: 'security'
  },
  {
    id: '5',
    type: 'success',
    title: 'Withdrawal Complete',
    description: 'Your withdrawal of 100 USDT has been processed',
    timestamp: '2 days ago',
    isUnread: false,
    iconType: 'withdrawal'
  }
];

const Notifications: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard');
  };

  const getIconColor = (iconType: string): string => {
    switch (iconType) {
      case 'swap':
      case 'withdrawal':
        return 'success';
      case 'alert':
      case 'security':
        return 'warning';
      case 'reward':
        return 'info';
      default:
        return 'info';
    }
  };

  const renderIcon = (iconType: string) => {
    switch (iconType) {
      case 'swap':
      case 'withdrawal':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7 10L12 15L17 10M12 15V3M5 21H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'alert':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M10.29 3.86L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55101 19.3437 1.64151 19.6871 1.81445 19.9905C1.98738 20.2939 2.23675 20.5467 2.53773 20.7239C2.83871 20.9011 3.18082 20.9961 3.53 21H20.47C20.8192 20.9961 21.1613 20.9011 21.4623 20.7239C21.7633 20.5467 22.0126 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86C13.5317 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3437 2.89725 12 2.89725C11.6563 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4683 3.56611 10.29 3.86Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 9V13M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'reward':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'security':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="notifications-container">
      <header className="notifications-header">
        <button 
          className="notifications-back-button" 
          onClick={handleBack}
          aria-label="Go back"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="notifications-title">Notifications</h1>
      </header>

      <div className="notifications-content">
        <div className="notifications-list">
          {mockNotifications.map((notification) => (
            <div 
              key={notification.id}
              className={`notification-item ${notification.isUnread ? 'unread' : 'read'}`}
            >
              <div className={`notification-icon notification-icon-${getIconColor(notification.iconType)}`}>
                {renderIcon(notification.iconType)}
              </div>
              <div className="notification-text">
                <div className="notification-header">
                  <h3 className="notification-title">{notification.title}</h3>
                  {notification.isUnread && (
                    <span className="notification-dot" aria-label="Unread"></span>
                  )}
                </div>
                <p className="notification-description">{notification.description}</p>
                <span className="notification-timestamp">{notification.timestamp}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Notifications;